import {useCallback, useEffect, useReducer, useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Button, ConfirmationModal, Tabs} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import type {
  AfriexCurrency,
  AfriexTransactionChannel,
  AfriexTransactionProcessor,
} from '@/types';
import {AfriexCurrencyCode, AfriexTransactionType} from '@/types';
import {capitalizeWords} from '@/utils/dashboard';
import {getResponseMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';

import {
  currencyFormReducer,
  initialCurrencyFormState,
  prepareSubmissionData,
} from './currencyReducer';
import TransactionTypeTab from './TransactionTypeTab';
import UpdateFormModal from './UpdateForm';

type SettingProps = {
  handleSave: (id: string, body: Partial<AfriexCurrency>) => Promise<void>;
  item: AfriexCurrency;
  fetching: boolean;
};

const Settings = ({item, handleSave, fetching}: SettingProps) => {
  const [showModal, setShowModal] = useState(false);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<{
    transactionType: AfriexTransactionType;
    channel: AfriexTransactionChannel;
    processor: AfriexTransactionProcessor;
  } | null>(null);

  const [state, dispatch] = useReducer(currencyFormReducer, {
    ...initialCurrencyFormState,
    business: item?.business ?? {},
  });

  // Sync state when item changes
  useEffect(() => {
    if (item?.business) {
      dispatch({
        type: 'INITIALIZE_FORM',
        payload: item,
      });
    }
  }, [item]);

  const handleRemoveProcessor = useCallback(
    (
      transactionType: AfriexTransactionType,
      channel: AfriexTransactionChannel,
    ) => {
      const currentProcessor =
        state.business[transactionType]?.[channel]?.currentProcessor;

      if (currentProcessor) {
        // Store the target for potential rollback
        setRemoveTarget({
          transactionType,
          channel,
          processor: currentProcessor,
        });

        // Optimistically update the state
        dispatch({
          type: 'REMOVE_BUSINESS_PROCESSOR',
          payload: {transactionType, channel},
        });

        // Show confirmation modal
        setShowRemoveConfirmation(true);
      }
    },
    [state.business],
  );

  const handleConfirmRemove = useCallback(async () => {
    if (!removeTarget) {
      return;
    }

    dispatch({type: 'SET_LOADING', payload: true});

    try {
      // Prepare the updated data for API submission
      const updateData = prepareSubmissionData(state, item);

      // Call the API with the updated business object
      await handleSave(item?.id as string, updateData);

      // Delay modal close to show success feedback
      setTimeout(() => {
        setShowRemoveConfirmation(false);
        setRemoveTarget(null);
      }, 1500);
    } catch (error) {
      // Rollback the optimistic update if API fails
      if (removeTarget) {
        dispatch({
          type: 'UPDATE_BUSINESS_PROCESSOR',
          payload: {
            transactionType: removeTarget.transactionType,
            channel: removeTarget.channel,
            processor: removeTarget.processor,
          },
        });
      }
      showErrorMessage(
        getResponseMessage(error) || 'Failed to remove processor',
      );
      // Keep modal open on error
    } finally {
      dispatch({type: 'SET_LOADING', payload: false});
    }
  }, [removeTarget, state, item, handleSave, dispatch]);

  const handleCancelRemove = useCallback(() => {
    // Rollback the optimistic update
    if (removeTarget) {
      dispatch({
        type: 'UPDATE_BUSINESS_PROCESSOR',
        payload: {
          transactionType: removeTarget.transactionType,
          channel: removeTarget.channel,
          processor: removeTarget.processor,
        },
      });
    }

    setShowRemoveConfirmation(false);
    setRemoveTarget(null);
  }, [removeTarget]);

  return (
    <>
      <div className="flex justify-end mb-6">
        <PermissionsProvider permission={AfriexAdminPermission.CurrencyUpdate}>
          <Button variant={'solid'} onClick={() => setShowModal(!showModal)}>
            Update Settings
          </Button>
        </PermissionsProvider>
      </div>
      <div className="flex flex-col gap-3">
        <div className="overflow-x-auto">
          {item?.code !== AfriexCurrencyCode.USD && (
            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
              <p className="text-slate-500 dark:text-slate-100 text-sm">
                Referral Payout Rate
              </p>
              <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
                {item?.params?.referralPayoutRate
                  ? `${item?.params?.referralPayoutRate} ${item?.code}`
                  : 'Not Set'}
              </p>
            </div>
          )}
          <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
            <p className="text-slate-500 dark:text-slate-100 text-sm">
              Withdrawal Fee Basis Points{' '}
            </p>
            <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
              {item?.params?.withdrawalFeeBasisPoints
                ? `${item?.params?.withdrawalFeeBasisPoints}`
                : 'Not Set'}
            </p>
          </div>
          <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
            <p className="text-slate-500 dark:text-slate-100 text-sm">
              Fixed Withdrawal Fee{' '}
            </p>
            <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
              {item?.params?.fixedWithdrawalFee
                ? `${item?.params?.fixedWithdrawalFee}`
                : 'Not Set'}
            </p>
          </div>
          <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
            <p className="text-slate-500 dark:text-slate-100 text-sm">
              Fixed Deposit Fee{' '}
            </p>
            <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
              {item?.params?.fixedDepositFee
                ? `${item?.params?.fixedDepositFee}`
                : 'Not Set'}
            </p>
          </div>
          <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
            <p className="text-slate-500 dark:text-slate-100 text-sm">
              Fee Structure{' '}
            </p>
            <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
              {item?.params?.feeStructure
                ? `${item?.params?.feeStructure}`
                : 'Not Set'}
            </p>
          </div>

          {/* Business Settings Section */}
          <div className="mt-6 mb-2">
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 px-3 py-2 border-b border-gray-300 dark:border-gray-600">
              Business Configuration
            </h3>
          </div>

          {/* Business Configuration Tabs - Display Only */}
          <Tabs
            tabs={[
              {
                id: 'withdraw',
                title: capitalizeWords(
                  AfriexTransactionType.WITHDRAW?.replace(/_/g, ' '),
                ),
                content: (
                  <TransactionTypeTab
                    transactionType={AfriexTransactionType.WITHDRAW}
                    business={state.business}
                    onRemoveProcessor={handleRemoveProcessor}
                    isLoading={fetching}
                  />
                ),
              },
              {
                id: 'deposit',
                title: capitalizeWords(
                  AfriexTransactionType.DEPOSIT?.replace(/_/g, ' '),
                ),
                content: (
                  <TransactionTypeTab
                    transactionType={AfriexTransactionType.DEPOSIT}
                    business={state.business}
                    onRemoveProcessor={handleRemoveProcessor}
                    isLoading={fetching}
                  />
                ),
              },
            ]}
          />
        </div>
      </div>

      <UpdateFormModal
        onClose={() => setShowModal(false)}
        showModal={showModal}
        handleSave={handleSave}
        item={item}
        externalState={state}
        externalDispatch={dispatch}
      />

      <ConfirmationModal
        isOpen={showRemoveConfirmation}
        title="Remove Processor"
        label={
          removeTarget ? (
            <>
              Remove <strong>{removeTarget.processor}</strong> from{' '}
              <strong>
                {capitalizeWords(removeTarget.channel?.replace(/_/g, ' '))}
              </strong>{' '}
              for{' '}
              <strong>
                {capitalizeWords(
                  removeTarget.transactionType?.replace(/_/g, ' '),
                )}
              </strong>{' '}
              transactions?
            </>
          ) : (
            'Remove processor configuration?'
          )
        }
        confirmAction={handleConfirmRemove}
        confirmActionLabel="Remove"
        cancelAction={handleCancelRemove}
        cancelActionLabel="Cancel"
        loading={state.isLoading}
        loadingLabel="Removing..."
      />
    </>
  );
};

export default Settings;
